<template>
    <div class="fr-page">
        <div class="fr-header">
            <h2>{{ $t('reports.title') }}</h2>
        </div>

        <!-- Filters -->
        <div class="fr-filters card">
            <div class="fr-filter-row">
                <div class="fr-field">
                    <label>ESIR</label>
                    <select v-model="selectedEsirId" @change="generate()">
                        <option value="">{{ $t('reports.selectEsir') }}</option>
                        <option value="ALL">{{ $t('reports.allEsirs') }}</option>
                        <option v-for="e in esirs" :key="e.id" :value="e.id">
                            {{ e.locationName || e.esirCode }} ({{ e.posNumber || e.esirCode }})
                        </option>
                    </select>
                </div>
                <div class="fr-field fr-date">
                    <label>{{ $t('common.from') }}</label>
                    <el-date-picker v-model="dateFrom" type="datetime" format="dd.MM.yyyy. HH:mm"
                        :placeholder="$t('common.from')" size="small" style="width:100%"></el-date-picker>
                </div>
                <div class="fr-field fr-date">
                    <label>{{ $t('common.to') }}</label>
                    <el-date-picker v-model="dateTo" type="datetime" format="dd.MM.yyyy. HH:mm"
                        :placeholder="$t('common.to')" size="small" style="width:100%"></el-date-picker>
                </div>
                <div class="fr-field fr-field-btn">
                    <button class="fr-btn-generate" @click="generate()" :disabled="loading">
                        <i class="tim-icons icon-chart-bar-32"></i>
                        {{ loading ? $t('reports.generating') : $t('reports.generate') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Report -->
        <div v-if="report" class="fr-report">
            <!-- Summary cards -->
            <div class="fr-cards">
                <div class="fr-card fr-card-sale">
                    <div class="frc-label">{{ $t('reports.cards.sales') }}</div>
                    <div class="frc-value">{{ formatPrice(report.totalSales) }}</div>
                    <div class="frc-count">{{ report.salesCount }} {{ $t('reports.byType.count').toLowerCase() }}</div>
                </div>
                <div class="fr-card fr-card-refund">
                    <div class="frc-label">{{ $t('reports.cards.refunds') }}</div>
                    <div class="frc-value">{{ formatPrice(report.totalRefunds) }}</div>
                    <div class="frc-count">{{ report.refundsCount }} {{ $t('reports.byType.count').toLowerCase() }}</div>
                </div>
                <div class="fr-card fr-card-net">
                    <div class="frc-label">{{ $t('reports.cards.net') }}</div>
                    <div class="frc-value">{{ formatPrice(report.netTotal) }}</div>
                    <div class="frc-count">{{ report.totalCount }} {{ $t('common.total').toLowerCase() }}</div>
                </div>
                <div class="fr-card fr-card-tax">
                    <div class="frc-label">{{ $t('reports.cards.tax') }}</div>
                    <div class="frc-value">{{ formatPrice(report.totalTax) }}</div>
                </div>
            </div>

            <!-- By invoice type -->
            <div class="fr-section card">
                <h3>{{ $t('reports.byType.title') }}</h3>
                <table class="fr-table">
                    <thead>
                        <tr><th>{{ $t('common.type') }}</th><th>{{ $t('reports.byType.sales') }}</th><th>{{ $t('reports.byType.refunds') }}</th><th>{{ $t('common.total') }}</th><th>{{ $t('reports.byType.count') }}</th></tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in report.byType" :key="row.type">
                            <td><strong>{{ typeLabel(row.type) }}</strong></td>
                            <td class="mono">{{ formatPrice(row.sales) }}</td>
                            <td class="mono" style="color:#dc3545">{{ formatPrice(row.refunds) }}</td>
                            <td class="mono"><strong>{{ formatPrice(row.net) }}</strong></td>
                            <td>{{ row.count }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Tax breakdown -->
            <div class="fr-section card">
                <h3>{{ $t('reports.taxBreakdown.title') }}</h3>
                <table class="fr-table">
                    <thead>
                        <tr><th>{{ $t('reports.taxBreakdown.label') }}</th><th>{{ $t('reports.taxBreakdown.base') }}</th><th>{{ $t('reports.taxBreakdown.tax') }}</th><th>{{ $t('common.total') }}</th></tr>
                    </thead>
                    <tbody>
                        <tr v-for="tax in report.taxBreakdown" :key="tax.label">
                            <td><span class="fr-tax-label">{{ tax.label }}</span></td>
                            <td class="mono">{{ formatPrice(tax.base) }}</td>
                            <td class="mono">{{ formatPrice(tax.tax) }}</td>
                            <td class="mono"><strong>{{ formatPrice(tax.total) }}</strong></td>
                        </tr>
                        <tr v-if="report.taxBreakdown.length === 0">
                            <td colspan="4" style="text-align:center;color:#adb5bd;padding:20px">{{ $t('reports.taxBreakdown.noData') }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- By payment type -->
            <div class="fr-section card">
                <h3>{{ $t('reports.byPayment.title') }}</h3>
                <table class="fr-table">
                    <thead><tr><th>{{ $t('reports.byPayment.method') }}</th><th>{{ $t('reports.byPayment.amount') }}</th><th>{{ $t('reports.byPayment.count') }}</th></tr></thead>
                    <tbody>
                        <tr v-for="pay in report.byPayment" :key="pay.type">
                            <td><strong>{{ pay.label }}</strong></td>
                            <td class="mono">{{ formatPrice(pay.amount) }}</td>
                            <td>{{ pay.count }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-else-if="!loading" class="fr-empty card">
            <i class="tim-icons icon-chart-bar-32"></i>
            <p>{{ $t('reports.selectHint') }}</p>
        </div>
    </div>
</template>

<script>
import axios from '@/util/axiosinstance'
import { DatePicker } from 'element-ui'

export default {
    name: 'FiscalReports',
    components: { 'el-date-picker': DatePicker },
    data() {
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        const yFrom = new Date(yesterday); yFrom.setHours(0,0,0,0)
        const yTo = new Date(yesterday); yTo.setHours(23,59,59,0)
        return {
            esirs: [],
            selectedEsirId: '',
            dateFrom: yFrom,
            dateTo: yTo,
            loading: false,
            report: null
        }
    },
    created() {
        axios.get('/api/v1/esirs').then(r => { this.esirs = r.data })
    },
    methods: {
        async generate() {
            if (!this.selectedEsirId) { this.report = null; return }
            this.loading = true

            try {
                const params = {}
                if (this.dateFrom) params.from = this.dateFrom instanceof Date ? this.dateFrom.toISOString() : new Date(this.dateFrom).toISOString()
                if (this.dateTo) params.to = this.dateTo instanceof Date ? this.dateTo.toISOString() : new Date(this.dateTo).toISOString()

                // Single request to server-side aggregation endpoint — no more loading 50,000 invoices
                const esirParam = this.selectedEsirId === 'ALL' ? 'all' : this.selectedEsirId
                const res = await axios.get('/api/v1/report/esir/' + esirParam, { params })

                this.report = res.data
            } catch(e) {
                console.error('Report generation failed:', e)
                this.report = null
            }
            this.loading = false
        },
        typeLabel(t) { return { Normal: this.$t('pos.types.normal'), Advance: this.$t('pos.types.advance'), Proforma: this.$t('pos.types.proforma'), Copy: this.$t('pos.types.copy'), Training: this.$t('pos.types.training') }[t] || t },
        formatPrice(v) { return Number(v||0).toLocaleString('sr-RS', {minimumFractionDigits:2, maximumFractionDigits:2}) }
    }
}
</script>

<style scoped>
.fr-page { padding: 15px; }
.fr-header h2 { margin: 0 0 15px; font-size: 20px; color: #2c3e50; }
.card { background: #fff; border-radius: 10px; border: 1px solid #e0e0e0; margin-bottom: 15px; }
.fr-filters { padding: 12px 15px; }
.fr-filter-row { display: flex; gap: 10px; align-items: flex-end; flex-wrap: wrap; }
.fr-field { flex: 1; min-width: 140px; }
.fr-field label { display: block; font-size: 10px; font-weight: 700; text-transform: uppercase; color: #6c757d; margin-bottom: 3px; }
.fr-field input, .fr-field select { width: 100%; padding: 7px 10px; border: 1px solid #dee2e6; border-radius: 6px; font-size: 13px; }
.fr-field input:focus, .fr-field select:focus { border-color: #1a73e8; outline: none; }
.fr-date .el-date-editor { width: 100% !important; }
.fr-field-btn { display: flex; align-items: flex-end; }
.fr-btn-generate {
    padding: 8px 20px; border: none; background: #1a73e8; color: #fff;
    border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer;
    display: flex; align-items: center; gap: 8px; white-space: nowrap; transition: all 0.15s;
}
.fr-btn-generate:hover { background: #1557b0; }
.fr-btn-generate:disabled { opacity: 0.5; cursor: not-allowed; }
/* Cards */
.fr-cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 15px; }
.fr-card {
    background: #fff; border-radius: 10px; border: 1px solid #e0e0e0;
    padding: 16px 18px; position: relative; overflow: hidden;
}
.fr-card::before {
    content: ''; position: absolute; top: 0; left: 0; width: 4px; height: 100%;
}
.fr-card-sale::before { background: #28a745; }
.fr-card-refund::before { background: #dc3545; }
.fr-card-net::before { background: #1a73e8; }
.fr-card-tax::before { background: #f0ad4e; }
.frc-label { font-size: 11px; font-weight: 700; text-transform: uppercase; color: #6c757d; margin-bottom: 4px; }
.frc-value { font-size: 22px; font-weight: 800; color: #2c3e50; font-family: monospace; }
.frc-count { font-size: 12px; color: #adb5bd; margin-top: 4px; }
/* Table */
.fr-section { padding: 15px 18px; }
.fr-section h3 { margin: 0 0 12px; font-size: 15px; color: #2c3e50; }
.fr-table { width: 100%; border-collapse: collapse; }
.fr-table th { padding: 8px 10px; font-size: 10px; font-weight: 700; text-transform: uppercase; color: #6c757d; background: #f8f9fa; border-bottom: 1px solid #e9ecef; text-align: left; }
.fr-table td { padding: 8px 10px; border-bottom: 1px solid #f1f3f5; font-size: 13px; color: #2c3e50; }
.mono { font-family: monospace; }
.fr-tax-label { background: #fff3cd; color: #856404; padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 700; }
/* Empty */
.fr-empty { text-align: center; padding: 60px 20px; color: #adb5bd; }
.fr-empty i { font-size: 40px; margin-bottom: 10px; display: block; }
/* Responsive */
@media (max-width: 900px) {
    .fr-cards { grid-template-columns: repeat(2, 1fr); }
}
</style>